import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
  updateDoc,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "../../config/firebase";

// Store active listeners to allow cleanup
const activeListeners: { [key: string]: Unsubscribe } = {};

// Helper to clean up listeners
export const cleanupOrgListeners = (listenerId?: string) => {
  if (listenerId && activeListeners[listenerId]) {
    activeListeners[listenerId]();
    delete activeListeners[listenerId];
  } else if (!listenerId) {
    // Clean up all listeners if no ID provided
    Object.values(activeListeners).forEach((unsubscribe) => unsubscribe());
    Object.keys(activeListeners).forEach((key) => delete activeListeners[key]);
  }
};

interface Organization {
  id: string;
  name: string;
  subscriptionId: string;
  url?: string;
  billingAddress?: {
    address_1?: string;
    address_2?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
  };
  users: {
    userId: string;
    role: "owner" | "member";
    joinedAt: any; // Timestamp
  }[];
  planType: "expert" | "customer";
  planName: string;
  status: "active" | "suspended" | "cancelled";
  wpCustomerId?: number;
  owner?: {
    owner?: string;
  };
  taxId?: string;
  // Other fields omitted for brevity
}

interface OrganizationWithStats extends Organization {
  totalSessions?: number;
  totalTeams?: number;
  wallet?: number;
  amount?: number;
  totalEarnings?: number;
  lastActivity?: string;
}

interface OrgState {
  organizations: OrganizationWithStats[];
  currentOrganization: OrganizationWithStats | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  updateStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  updateError: string | null;
  // Cache for different plan types
  customerOrganizationsCache: OrganizationWithStats[];
  expertOrganizationsCache: OrganizationWithStats[];
  lastLoadedPlanType: "customer" | "expert" | null;
}

const initialState: OrgState = {
  organizations: [],
  currentOrganization: null,
  status: "idle",
  updateStatus: "idle",
  error: null,
  updateError: null,
  customerOrganizationsCache: [],
  expertOrganizationsCache: [],
  lastLoadedPlanType: null,
};

// Fetch organizations for the user
export const fetchUserOrganizations = createAsyncThunk(
  "org/fetchUserOrganizations",
  async (
    { uid, orgId }: { uid: string; orgId?: string },
    { rejectWithValue }
  ) => {
    try {
      // If orgId is provided, fetch only that specific organization
      if (orgId) {
        const orgDocRef = doc(db, "organizations", orgId);
        const orgDoc = await getDoc(orgDocRef);

        if (!orgDoc.exists()) {
          return rejectWithValue("Organization not found");
        }
        const orgData = {
          id: orgDoc.id,
          ...orgDoc.data(),
        } as Organization;

        console.log("Fetched organization data:", orgData);
        return [orgData];
      }

      // Otherwise, get user document to access organization IDs
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        return rejectWithValue("User not found");
      }

      const userData = userDoc.data();
      const orgIds = Object.keys(userData.organizations || {});

      if (orgIds.length === 0) {
        return [];
      }

      // Fetch all organizations the user belongs to
      const organizations: Organization[] = [];

      for (const orgId of orgIds) {
        const orgDocRef = doc(db, "organizations", orgId);
        const orgDoc = await getDoc(orgDocRef);

        if (orgDoc.exists()) {
          organizations.push({
            id: orgDoc.id,
            ...orgDoc.data(),
          } as Organization);
        }
      }

      return organizations;
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

// Subscribe to real-time organization updates
export const subscribeToOrganization = createAsyncThunk(
  "org/subscribeToOrganization",
  async (
    { orgId, listenerId = "default" }: { orgId: string; listenerId?: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Clean up any existing listener with the same ID
      if (activeListeners[listenerId]) {
        cleanupOrgListeners(listenerId);
      }

      // Set up a new listener
      const orgDocRef = doc(db, "organizations", orgId);

      const unsubscribe = onSnapshot(
        orgDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const orgData = {
              id: docSnapshot.id,
              ...docSnapshot.data(),
            } as OrganizationWithStats;

            // Dispatch action to update the organization in the store
            dispatch(setCurrentOrganization(orgData));
            console.log("Real-time organization update received:", orgData);
          } else {
            console.warn(
              `Organization with ID ${orgId} does not exist or was deleted`
            );
          }
        },
        (error) => {
          console.error("Error in organization listener:", error);
        }
      );

      // Store the unsubscribe function
      activeListeners[listenerId] = unsubscribe;

      // Return the listener ID for reference
      return listenerId;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to subscribe to organization updates"
      );
    }
  }
);

// Update organization data
export const updateOrganization = createAsyncThunk(
  "org/updateOrganization",
  async (
    {
      orgId,
      orgData,
    }: {
      orgId: string;
      orgData: {
        name: string;
        url?: string;
        subscriptionId?: string;
        taxId?: string;
        teamName?: string;
        billingAddress: {
          address_1: string;
          address_2?: string;
          country: string;
          state: string;
          city: string;
          postcode: string;
        };
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const orgDocRef = doc(db, "organizations", orgId); // Prepare update data
      const updateData: any = {
        name: orgData.name,
        url: orgData.url || "",
        billingAddress: {
          address_1: orgData.billingAddress.address_1,
          address_2: orgData.billingAddress.address_2 || "",
          country: orgData.billingAddress.country,
          state: orgData.billingAddress.state,
          city: orgData.billingAddress.city,
          postcode: orgData.billingAddress.postcode,
        },
      };

      // Only add taxId if it's provided
      if (orgData.taxId) {
        updateData.taxId = orgData.taxId;
      }

      // Only add teamName if it's provided
      if (orgData.teamName) {
        updateData.teamName = orgData.teamName;
      }

      // Only add subscriptionId if it's provided and not undefined
      if (
        orgData.subscriptionId !== undefined &&
        orgData.subscriptionId !== null
      ) {
        updateData.subscriptionId = orgData.subscriptionId;
      }

      // Update the organization document with new data
      await updateDoc(orgDocRef, updateData);

      // Get the updated document
      const updatedOrgDoc = await getDoc(orgDocRef);

      if (!updatedOrgDoc.exists()) {
        return rejectWithValue("Organization not found after update");
      }

      return {
        id: updatedOrgDoc.id,
        ...updatedOrgDoc.data(),
      };
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

// Subscribe to real-time organizations by plan type
export const subscribeToOrganizationsByPlanType = createAsyncThunk(
  "org/subscribeToOrganizationsByPlanType",
  async (
    { planType, listenerId = "orgsByPlanType" }: { planType: "expert" | "customer"; listenerId?: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Clean up any existing listener with the same ID
      if (activeListeners[listenerId]) {
        cleanupOrgListeners(listenerId);
      }

      const q = query(
        collection(db, "organizations"),
        where("planType", "==", planType)
      );

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          if (querySnapshot.empty) {
            dispatch(setOrganizations([]));
            return;
          }

          const organizations: OrganizationWithStats[] = [];
          const orgIds = querySnapshot.docs.map((doc) => doc.id);
          const orgDocs = querySnapshot.docs;

          // Batch fetch session activities in chunks of 10 (Firestore 'in' query limit)
          const sessionChunks = [];
          for (let i = 0; i < orgIds.length; i += 10) {
            sessionChunks.push(orgIds.slice(i, i + 10));
          }

          // Fetch all session activities in parallel
          const sessionPromises = sessionChunks.map((chunk) => {
            let chunkSessionsQuery;
            if (planType === "customer") {
              chunkSessionsQuery = query(
                collection(db, "sessionActivity"),
                where("customerOrganizationId", "in", chunk)
              );
            } else {
              chunkSessionsQuery = query(
                collection(db, "sessionActivity"),
                where("expertOrganizationId", "in", chunk)
              );
            }
            return getDocs(chunkSessionsQuery);
          });
          const sessionSnapshots = await Promise.all(sessionPromises);

          // Group sessions by organization ID
          const sessionsByOrg: {
            [orgId: string]: {
              count: number;
              totalCost: number;
              latestStartTime: any;
            };
          } = {};
          sessionSnapshots.forEach((chunkSnapshot) => {
            chunkSnapshot.forEach((sessionDoc) => {
              const sessionData = sessionDoc.data();
              const orgId =
                planType === "customer"
                  ? sessionData.customerOrganizationId
                  : sessionData.expertOrganizationId;
              if (orgId && !sessionsByOrg[orgId]) {
                sessionsByOrg[orgId] = {
                  count: 0,
                  totalCost: 0,
                  latestStartTime: null,
                };
              }
              if (orgId) {
                sessionsByOrg[orgId].count++;
                if (sessionData.cost && typeof sessionData.cost === "number") {
                  sessionsByOrg[orgId].totalCost += sessionData.cost;
                }
                if (sessionData.startTime) {
                  const currentStartTime = sessionData.startTime;
                  if (
                    !sessionsByOrg[orgId].latestStartTime ||
                    currentStartTime.toDate() >
                      sessionsByOrg[orgId].latestStartTime?.toDate()
                  ) {
                    sessionsByOrg[orgId].latestStartTime = currentStartTime;
                  }
                }
              }
            });
          });

          // Fetch all teams in parallel
          const teamsPromises = orgIds.map((orgId) => {
            const teamsCollection = collection(db, `organizations/${orgId}/teams`);
            return getDocs(teamsCollection);
          });
          const teamsSnapshots = await Promise.all(teamsPromises);
          const teamsByOrg: { [orgId: string]: number } = {};
          orgIds.forEach((orgId, idx) => {
            teamsByOrg[orgId] = teamsSnapshots[idx].size;
          });

          // For all organizations, fetch all users' wallet balances and sum them
          let walletsByOrg: { [orgId: string]: number } = {};
          for (const docSnap of orgDocs) {
            const orgData = {
              id: docSnap.id,
              ...docSnap.data(),
            } as OrganizationWithStats;
            const userIds = orgData.users?.map((u) => u.userId) || [];
            let walletSum = 0;
            if (userIds.length > 0) {
              const walletPromises = userIds.map((userId) => {
                const walletDocRef = doc(db, "wallets", userId);
                return getDoc(walletDocRef);
              });
              const walletDocs = await Promise.all(walletPromises);
              walletDocs.forEach((walletDoc) => {
                if (walletDoc.exists()) {
                  const walletData = walletDoc.data();
                  walletSum += walletData.balance || 0;
                }
              });
            }
            walletsByOrg[orgData.id] = walletSum;
          }

          // Process each organization
          for (const docSnap of orgDocs) {
            const orgData = {
              id: docSnap.id,
              ...docSnap.data(),
            } as OrganizationWithStats;

            // Get session data from batch result
            let sessionCount = 0;
            let totalAmount = 0;
            let lastActivityTimestamp: any = null;
            if (sessionsByOrg[docSnap.id]) {
              sessionCount = sessionsByOrg[docSnap.id].count;
              totalAmount = sessionsByOrg[docSnap.id].totalCost;
              lastActivityTimestamp = sessionsByOrg[docSnap.id].latestStartTime;
            }
            orgData.totalSessions = sessionCount;
            orgData.totalTeams = teamsByOrg[docSnap.id] || 0;

            // Format last activity date
            if (lastActivityTimestamp) {
              try {
                const date = lastActivityTimestamp.toDate();
                orgData.lastActivity = date.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                });
              } catch (error) {
                orgData.lastActivity = "-";
              }
            } else {
              orgData.lastActivity = "-";
            }

            // Set wallet field for all organizations
            orgData.wallet = walletsByOrg[orgData.id] || 0;

            // For customer organizations, set amount
            if (planType === "customer") {
              orgData.amount = totalAmount;
            }

            // For expert organizations, set total earnings
            if (planType === "expert") {
              orgData.totalEarnings = totalAmount;
            }

            organizations.push(orgData);
          }

          // Dispatch action to update organizations in the store with plan type
          dispatch(setOrganizations({ organizations, planType }));
        },
        (error) => {
          console.error("Error in organizations listener:", error);
        }
      );

      // Store the unsubscribe function
      activeListeners[listenerId] = unsubscribe;

      return listenerId;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to subscribe to organizations"
      );
    }
  }
);

// Fetch organizations by plan type with optimized data fetching (one-time fetch for initial load)
export const fetchOrganizationsByPlanType = createAsyncThunk(
  "org/fetchOrganizationsByPlanType",
  async (
    { planType }: { planType: "expert" | "customer" },
    { rejectWithValue }
  ) => {
    try {
      const q = query(
        collection(db, "organizations"),
        where("planType", "==", planType)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return [];
      }

      const organizations: OrganizationWithStats[] = [];
      const orgIds = querySnapshot.docs.map((doc) => doc.id);
      const orgDocs = querySnapshot.docs;

      // Batch fetch session activities in chunks of 10 (Firestore 'in' query limit)
      const sessionChunks = [];
      for (let i = 0; i < orgIds.length; i += 10) {
        sessionChunks.push(orgIds.slice(i, i + 10));
      }

      // Fetch all session activities in parallel
      const sessionPromises = sessionChunks.map((chunk) => {
        let chunkSessionsQuery;
        if (planType === "customer") {
          chunkSessionsQuery = query(
            collection(db, "sessionActivity"),
            where("customerOrganizationId", "in", chunk)
          );
        } else {
          chunkSessionsQuery = query(
            collection(db, "sessionActivity"),
            where("expertOrganizationId", "in", chunk)
          );
        }
        return getDocs(chunkSessionsQuery);
      });
      const sessionSnapshots = await Promise.all(sessionPromises);

      // Group sessions by organization ID
      const sessionsByOrg: {
        [orgId: string]: {
          count: number;
          totalCost: number;
          latestStartTime: any;
        };
      } = {};
      sessionSnapshots.forEach((chunkSnapshot) => {
        chunkSnapshot.forEach((sessionDoc) => {
          const sessionData = sessionDoc.data();
          const orgId =
            planType === "customer"
              ? sessionData.customerOrganizationId
              : sessionData.expertOrganizationId;
          if (orgId && !sessionsByOrg[orgId]) {
            sessionsByOrg[orgId] = {
              count: 0,
              totalCost: 0,
              latestStartTime: null,
            };
          }
          if (orgId) {
            sessionsByOrg[orgId].count++;
            if (sessionData.cost && typeof sessionData.cost === "number") {
              sessionsByOrg[orgId].totalCost += sessionData.cost;
            }
            if (sessionData.startTime) {
              const currentStartTime = sessionData.startTime;
              if (
                !sessionsByOrg[orgId].latestStartTime ||
                currentStartTime.toDate() >
                  sessionsByOrg[orgId].latestStartTime?.toDate()
              ) {
                sessionsByOrg[orgId].latestStartTime = currentStartTime;
              }
            }
          }
        });
      });

      // Fetch all teams in parallel
      const teamsPromises = orgIds.map((orgId) => {
        const teamsCollection = collection(db, `organizations/${orgId}/teams`);
        return getDocs(teamsCollection);
      });
      const teamsSnapshots = await Promise.all(teamsPromises);
      const teamsByOrg: { [orgId: string]: number } = {};
      orgIds.forEach((orgId, idx) => {
        teamsByOrg[orgId] = teamsSnapshots[idx].size;
      });

      // For all organizations, fetch all users' wallet balances and sum them
      let walletsByOrg: { [orgId: string]: number } = {};
      for (const docSnap of orgDocs) {
        const orgData = {
          id: docSnap.id,
          ...docSnap.data(),
        } as OrganizationWithStats;
        const userIds = orgData.users?.map((u) => u.userId) || [];
        let walletSum = 0;
        if (userIds.length > 0) {
          const walletPromises = userIds.map((userId) => {
            const walletDocRef = doc(db, "wallets", userId);
            return getDoc(walletDocRef);
          });
          const walletDocs = await Promise.all(walletPromises);
          walletDocs.forEach((walletDoc) => {
            if (walletDoc.exists()) {
              const walletData = walletDoc.data();
              walletSum += walletData.balance || 0;
            }
          });
        }
        walletsByOrg[orgData.id] = walletSum;
      }

      // Process each organization
      for (const docSnap of orgDocs) {
        const orgData = {
          id: docSnap.id,
          ...docSnap.data(),
        } as OrganizationWithStats;

        // Get session data from batch result
        let sessionCount = 0;
        let totalAmount = 0;
        let lastActivityTimestamp: any = null;
        if (sessionsByOrg[docSnap.id]) {
          sessionCount = sessionsByOrg[docSnap.id].count;
          totalAmount = sessionsByOrg[docSnap.id].totalCost;
          lastActivityTimestamp = sessionsByOrg[docSnap.id].latestStartTime;
        }
        orgData.totalSessions = sessionCount;
        orgData.totalTeams = teamsByOrg[docSnap.id] || 0;

        // Format last activity date
        if (lastActivityTimestamp) {
          try {
            const date = lastActivityTimestamp.toDate();
            orgData.lastActivity = date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
          } catch (error) {
            orgData.lastActivity = "-";
          }
        } else {
          orgData.lastActivity = "-";
        }

        // Set wallet field for all organizations
        orgData.wallet = walletsByOrg[orgData.id] || 0;

        // For customer organizations, set amount
        if (planType === "customer") {
          orgData.amount = totalAmount;
        }

        // For expert organizations, set total earnings
        if (planType === "expert") {
          orgData.totalEarnings = totalAmount;
        }

        organizations.push(orgData);
      }

      return organizations;
    } catch (error: any) {
      console.error("Error fetching organizations:", error);
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

// Update team data in teams subcollection
export const updateTeamData = createAsyncThunk(
  "org/updateTeamData",
  async (
    {
      orgId,
      teamId,
      teamData,
    }: {
      orgId: string;
      teamId: string;
      teamData: {
        teamName?: string;
        [key: string]: any;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const teamDocRef = doc(db, `organizations/${orgId}/teams`, teamId);

      // Check if team document exists
      const teamDoc = await getDoc(teamDocRef);
      if (!teamDoc.exists()) {
        return rejectWithValue("Team document not found");
      }

      // Update the team document
      await updateDoc(teamDocRef, teamData);

      // Get the updated document
      const updatedTeamDoc = await getDoc(teamDocRef);

      if (!updatedTeamDoc.exists()) {
        return rejectWithValue("Team not found after update");
      }

      return {
        id: updatedTeamDoc.id,
        orgId,
        ...updatedTeamDoc.data(),
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "An error occurred updating team"
      );
    }
  }
);

// Update organization by taxId with fallback to teams subcollection
export const updateOrganizationByTaxId = createAsyncThunk(
  "org/updateOrganizationByTaxId",
  async (
    {
      taxId,
      orgData,
      teamData,
    }: {
      taxId: string;
      orgData: {
        name: string;
        url?: string;
        subscriptionId?: string;
        taxId?: string;
        teamName?: string;
        billingAddress: {
          address_1: string;
          address_2?: string;
          country: string;
          state: string;
          city: string;
          postcode: string;
        };
      };
      teamData?: {
        teamName?: string;
      };
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // First, try to find organization by taxId
      const orgQuery = query(
        collection(db, "organizations"),
        where("taxId", "==", taxId)
      );
      const orgSnapshot = await getDocs(orgQuery);

      if (!orgSnapshot.empty) {
        // Organization found, update it
        const orgDoc = orgSnapshot.docs[0];
        const orgDocRef = doc(db, "organizations", orgDoc.id);

        const updateData: any = {
          name: orgData.name,
          url: orgData.url || "",
          subscriptionId: orgData.subscriptionId,
          billingAddress: {
            address_1: orgData.billingAddress.address_1,
            address_2: orgData.billingAddress.address_2 || "",
            country: orgData.billingAddress.country,
            state: orgData.billingAddress.state,
            city: orgData.billingAddress.city,
            postcode: orgData.billingAddress.postcode,
          },
        };

        if (orgData.taxId) {
          updateData.taxId = orgData.taxId;
        }

        // Add teamName to organization document if provided
        if (orgData.teamName) {
          updateData.teamName = orgData.teamName;
        }

        await updateDoc(orgDocRef, updateData);

        // If teamData is provided, also update the team in the subcollection
        if (teamData && teamData.teamName) {
          const orgDataFromDoc = orgDoc.data();
          const ownerId = orgDataFromDoc.owner?.owner;

          if (ownerId) {
            try {
              await dispatch(
                updateTeamData({
                  orgId: orgDoc.id,
                  teamId: ownerId,
                  teamData: { teamName: teamData.teamName },
                })
              );
            } catch (teamError) {
              console.warn("Failed to update team data:", teamError);
            }
          }
        }

        // Get the updated organization document
        const updatedOrgDoc = await getDoc(orgDocRef);
        if (!updatedOrgDoc.exists()) {
          return rejectWithValue("Organization not found after update");
        }

        return {
          id: updatedOrgDoc.id,
          ...updatedOrgDoc.data(),
        };
      } else {
        return rejectWithValue("Organization not found with provided taxId");
      }
    } catch (error: any) {
      return rejectWithValue(error?.message || "An error occurred");
    }
  }
);

const orgSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    setCurrentOrganization: (state, action) => {
      state.currentOrganization = action.payload;
    },
    setOrganizations: (state, action) => {
      const { organizations: orgs, planType } = action.payload;
      state.organizations = orgs;
      state.status = "succeeded";
      state.error = null;
      state.lastLoadedPlanType = planType;
      
      // Update cache for the specific plan type
      if (planType === "customer") {
        state.customerOrganizationsCache = orgs;
      } else if (planType === "expert") {
        state.expertOrganizationsCache = orgs;
      }
    },
    clearOrganizations: (state) => {
      state.organizations = [];
      state.status = "idle";
    },
    switchPlanType: (state, action) => {
      const planType = action.payload as "customer" | "expert";
      
      // If we have cached data for this plan type, use it immediately
      if (planType === "customer" && state.customerOrganizationsCache.length > 0) {
        state.organizations = state.customerOrganizationsCache;
        state.lastLoadedPlanType = planType;
      } else if (planType === "expert" && state.expertOrganizationsCache.length > 0) {
        state.organizations = state.expertOrganizationsCache;
        state.lastLoadedPlanType = planType;
      } else {
        // No cache available, set to loading
        state.organizations = [];
        state.status = "loading";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserOrganizations.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserOrganizations.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.organizations = action.payload;
        state.error = null;

        // Set current organization logic
        if (action.payload.length > 0) {
          // If we have organizations and no current organization is set
          if (!state.currentOrganization) {
            state.currentOrganization = action.payload[0];
          } else {
            // Update current organization if it exists in the new data
            const updatedCurrentOrg = action.payload.find(
              (org) => org.id === state.currentOrganization?.id
            );
            if (updatedCurrentOrg) {
              state.currentOrganization = updatedCurrentOrg;
            } else {
              // If current org not found in new data, set to first available
              state.currentOrganization = action.payload[0];
            }
          }
        }
      })
      .addCase(fetchUserOrganizations.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      // Handle subscribeToOrganization states
      .addCase(subscribeToOrganization.pending, (state) => {
        state.status = "loading";
      })
      .addCase(subscribeToOrganization.fulfilled, (state) => {
        state.status = "succeeded";
        // No need to update state here as the listener will dispatch setCurrentOrganization actions
      })
      .addCase(subscribeToOrganization.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      // Handle updateOrganization states
      .addCase(updateOrganization.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        // Always set currentOrganization to the latest payload from DB
        state.currentOrganization = action.payload as OrganizationWithStats;

        // Update or add the organization in the organizations array
        const index = state.organizations.findIndex(
          (org) => org.id === action.payload.id
        );
        if (index !== -1) {
          state.organizations[index] = action.payload as OrganizationWithStats;
        } else {
          state.organizations.push(action.payload as OrganizationWithStats);
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload as string;
      })
      .addCase(fetchOrganizationsByPlanType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchOrganizationsByPlanType.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.organizations = action.payload;
        state.error = null;
      })
      .addCase(fetchOrganizationsByPlanType.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      // Handle subscribeToOrganizationsByPlanType states
      .addCase(subscribeToOrganizationsByPlanType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(subscribeToOrganizationsByPlanType.fulfilled, (state) => {
        state.status = "succeeded";
        // The listener will dispatch setOrganizations actions
      })
      .addCase(subscribeToOrganizationsByPlanType.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(updateTeamData.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateTeamData.fulfilled, (state) => {
        state.updateStatus = "succeeded";
        // Optionally, handle any state updates needed after team data update
      })
      .addCase(updateTeamData.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload as string;
      })
      .addCase(updateOrganizationByTaxId.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateOrganizationByTaxId.fulfilled, (state) => {
        state.updateStatus = "succeeded";
        // Optionally, handle any state updates needed after organization update by taxId
      })
      .addCase(updateOrganizationByTaxId.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload as string;
      });
  },
});

export const { setCurrentOrganization, setOrganizations, clearOrganizations, switchPlanType } = orgSlice.actions;
export default orgSlice.reducer;
